/**
 * O botão "Modelo" do "Próximos dias": o modelo do TEMPO — IFS (o padrão),
 * AIFS (IA do ECMWF), GFS (NOAA) ou ICON (DWD). Pedidos do usuário
 * (4/11/2026): "um botão no canto direito, na aba de Próximos dias, para
 * alternar entre os modelos"; com quatro, "no celular, ao clicar no botão,
 * abrir uma caixa de seleção". Proposta e julgamento:
 * `docs/proposta-previsao-modelos.md` (e o adendo).
 *
 * - Desktop: logo abaixo da aba "Próximos dias", uma peça só com as opções
 *   empilhadas (o título "Modelo" em cima): a marcada PREENCHIDA de violeta — a
 *   aba de sair do modo tem só a borda, para não se confundirem. Setas andam.
 * - Celular: o 4º botão da coluna da direita (no modo os focos saem), com a
 *   sigla do modelo em uso e ⌄; o toque abre a caixa de seleção à esquerda
 *   dele (Esc, tocar fora ou no botão fecham; escolher aplica e fecha).
 * - Só no modo e com dois ou mais modelos no índice; travado na exportação.
 * - Quem volta com outro modelo lembrado (preferências) é avisado ao entrar.
 */
import clsx from 'clsx';
import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useExportLock } from '../loop/playback';
import { showToast } from '../state/toasts';
import { BOTAO_DA_COLUNA_PX, VAO_DA_COLUNA_PX } from '../ui/layoutCelular';
import { useModalLayer } from '../ui/modalStack';
import { marcarVisto, useEhNovidade } from '../ui/novidade';
import { useDeitado } from '../ui/useMediaQuery';
import { SOBRE_MODELO_TEMPO, modelosTempoNoIndice, temEscolhaDeModelo, type ModeloTempo } from './api';
import { trocarModeloTempo } from './motor';
import { tempoEmUso, usePrevisaoStore } from './store';

/** Selo de novidade nos modelos novos (some na primeira troca). */
const NOVIDADE = { id: 'previsao-modelos', ate: '2027-01-31' } as const;

function nomeComIa(m: ModeloTempo): string {
  const s = SOBRE_MODELO_TEMPO[m];
  return `${s.nome}${s.ia ? ' (IA)' : ''}`;
}

function avisar(m: ModeloTempo, motivo: 'troca' | 'lembrado'): void {
  const s = SOBRE_MODELO_TEMPO[m];
  showToast({
    id: 'previsao-modelo',
    kind: 'info',
    timeoutMs: motivo === 'troca' ? 3000 : 5000,
    message:
      motivo === 'troca'
        ? `Tempo: ${s.nome}${m === 'aifs' ? ', o modelo de IA do ECMWF' : ` (${s.linha})`}.`
        : `Tempo: ${nomeComIa(m)}, a sua última escolha. Troque em “Modelo”.`,
  });
}

/** O aviso do modelo lembrado já saiu nesta visita (ou a pessoa já trocou: aí não é mais "lembrado"). */
let avisouLembrado = false;

function trocar(m: ModeloTempo): void {
  if (usePrevisaoStore.getState().tempo === m) return;
  marcarVisto(NOVIDADE.id);
  avisouLembrado = true;
  trocarModeloTempo(m);
  if (usePrevisaoStore.getState().tempo === m) avisar(m, 'troca');
}

/** Os modelos do botão (no modo, com escolha), ou nenhum. */
function useModelos(): ModeloTempo[] {
  const indice = usePrevisaoStore((s) => s.indice);
  const modo = usePrevisaoStore((s) => s.modo);
  return modo && temEscolhaDeModelo(indice) ? modelosTempoNoIndice(indice) : [];
}

/** Uma vez por visita: entrou no modo com outro modelo lembrado das preferências — avisa. */
function useAvisoDoLembrado(): void {
  const lembrado = usePrevisaoStore((s) => (s.modo && temEscolhaDeModelo(s.indice) && tempoEmUso(s) !== 'ifs' ? tempoEmUso(s) : null));
  useEffect(() => {
    if (!lembrado || avisouLembrado) return;
    avisouLembrado = true;
    avisar(lembrado, 'lembrado');
  }, [lembrado]);
}

function useTravado(): boolean {
  const exportando = usePrevisaoStore((s) => s.exportando);
  return useExportLock() || exportando;
}

/** Setas: o anterior (↑ ←) ou o seguinte (↓ →) da lista, em volta; Home/End nas pontas. −1 = outra tecla. */
function vizinho(e: KeyboardEvent, n: number, k: number): number {
  if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') return (k - 1 + n) % n;
  if (e.key === 'ArrowDown' || e.key === 'ArrowRight') return (k + 1) % n;
  if (e.key === 'Home') return 0;
  if (e.key === 'End') return n - 1;
  return -1;
}

/** Desktop: as opções, abaixo da aba "Próximos dias" (na pilha das abas da direita). */
export function AbaDoModelo() {
  const modelos = useModelos();
  const tempo = usePrevisaoStore(tempoEmUso);
  const travado = useTravado();
  const novidade = useEhNovidade(NOVIDADE.id, NOVIDADE.ate);
  const refs = useRef<Partial<Record<ModeloTempo, HTMLButtonElement | null>>>({});
  useAvisoDoLembrado();
  if (modelos.length < 2) return null;

  // Grupo de opção única: só a marcada entra no Tab; as setas trocam e levam o foco.
  const teclas = (e: KeyboardEvent) => {
    if (travado) return;
    const k = vizinho(e, modelos.length, Math.max(0, modelos.indexOf(tempo)));
    if (k < 0) return;
    e.preventDefault();
    const m = modelos[k]!;
    trocar(m);
    refs.current[m]?.focus();
  };

  return (
    <>
      <div
        role="radiogroup"
        aria-label="Modelo do tempo"
        aria-disabled={travado || undefined}
        data-aba-modelo=""
        onKeyDown={teclas}
        className="mt-2 flex w-9 shrink-0 flex-col overflow-hidden rounded-l-lg border border-r-0 border-ink-600/90 bg-ink-900/95 shadow-lg"
        title={travado ? 'Indisponível durante a exportação' : undefined}
      >
        <span className="px-0.5 pt-1 pb-0.5 text-center text-[8.5px] leading-none font-medium tracking-tight text-ink-300" aria-hidden>
          Modelo
        </span>
        {modelos.map((m) => {
          const marcado = tempo === m;
          const sobre = SOBRE_MODELO_TEMPO[m];
          return (
            <button
              key={m}
              ref={(el) => {
                refs.current[m] = el;
              }}
              type="button"
              role="radio"
              aria-checked={marcado}
              tabIndex={marcado ? 0 : -1}
              disabled={travado}
              onClick={() => trocar(m)}
              aria-label={`${sobre.nome}: ${sobre.detalhe}`}
              title={`${sobre.nome} — ${sobre.detalhe}`}
              data-modelo={m}
              className={clsx(
                'relative flex h-10 flex-col items-center justify-center leading-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-violet-400 disabled:opacity-40',
                marcado ? 'bg-violet-600 text-white' : 'text-ink-200 hover:bg-ink-800',
              )}
            >
              <span className="text-[10.5px] font-semibold">{sobre.curto}</span>
              {sobre.ia && <span className={clsx('mt-0.5 text-[8px]', marcado ? 'text-violet-100' : 'text-ink-400')}>IA</span>}
              {m !== 'ifs' && novidade && !marcado && <span className="absolute top-1 right-1 size-1.5 rounded-full bg-violet-400" aria-hidden />}
            </button>
          );
        })}
      </div>
      {/* Fora do grupo de opções: o leitor de tela anuncia a troca (o aviso de 3 s não é para ele). */}
      <span className="sr-only" aria-live="polite">
        Modelo do tempo: {nomeComIa(tempo)}
      </span>
    </>
  );
}

/** Topo do 4º botão da coluna (o da previsão é o 3º, no modo). */
const TOPO_DO_BOTAO = `calc(env(safe-area-inset-top) + 12px + ${3 * (BOTAO_DA_COLUNA_PX + VAO_DA_COLUNA_PX)}px)`;

/** Celular: o 4º botão da coluna da direita, que abre a caixa de seleção do modelo. */
export function BotaoDoModeloNaColuna() {
  const modelos = useModelos();
  const tempo = usePrevisaoStore(tempoEmUso);
  const travado = useTravado();
  const novidade = useEhNovidade(NOVIDADE.id, NOVIDADE.ate);
  const deitado = useDeitado();
  const [aberta, setAberta] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const caixa = useRef<HTMLDivElement>(null);
  const botao = useRef<HTMLButtonElement>(null);
  const opcoes = useRef<Partial<Record<ModeloTempo, HTMLButtonElement | null>>>({});
  const id = useId();
  useAvisoDoLembrado();

  const fechar = (devolverFoco = true) => {
    setAberta(false);
    if (devolverFoco) botao.current?.focus();
  };
  useModalLayer(aberta, { onEscape: () => fechar(), modal: false, ref: caixa });

  // Tocar fora fecha — e só fecha: o toque não chega ao mapa (abriria a ficha de um ponto) nem a outro botão.
  useEffect(() => {
    if (!aberta) return;
    const fora = (e: PointerEvent) => {
      if (raiz.current?.contains(e.target as Node)) return;
      e.preventDefault();
      e.stopPropagation();
      const engolir = (c: Event) => {
        c.preventDefault();
        c.stopPropagation();
      };
      window.addEventListener('click', engolir, { capture: true, once: true });
      // Sem clique depois (arrasto, toque longo): o engolidor não fica esperando o próximo toque.
      window.setTimeout(() => window.removeEventListener('click', engolir, true), 600);
      setAberta(false);
    };
    window.addEventListener('pointerdown', fora, true);
    return () => window.removeEventListener('pointerdown', fora, true);
  }, [aberta]);

  // Ao abrir, o foco vai à opção marcada.
  useEffect(() => {
    if (aberta) opcoes.current[tempoEmUso(usePrevisaoStore.getState())]?.focus();
  }, [aberta]);

  // Exportar ou perder a escolha (sair do modo) fecha a caixa.
  const fechada = travado || modelos.length < 2;
  useEffect(() => {
    if (fechada) setAberta(false);
  }, [fechada]);

  if (modelos.length < 2) return null;
  const atual = SOBRE_MODELO_TEMPO[tempo];

  const teclas = (e: KeyboardEvent) => {
    const comFoco = modelos.findIndex((m) => opcoes.current[m] === document.activeElement);
    const k = vizinho(e, modelos.length, Math.max(0, comFoco));
    if (k < 0) return;
    e.preventDefault();
    opcoes.current[modelos[k]!]?.focus();
  };

  return (
    <div ref={raiz}>
      <button
        ref={botao}
        type="button"
        onClick={() => setAberta((v) => !v)}
        disabled={travado}
        aria-haspopup="dialog"
        aria-expanded={aberta}
        aria-controls={aberta ? id : undefined}
        aria-label={`Modelo do tempo: ${atual.curto}${atual.ia ? ' (IA)' : ''}. Escolher o modelo`}
        title={travado ? 'Indisponível durante a exportação' : `Modelo do tempo: ${nomeComIa(tempo)}`}
        data-coluna-do-mapa=""
        data-botao-modelo-celular={tempo}
        className={clsx(
          'absolute right-[calc(8px+env(safe-area-inset-right))] z-10 inline-flex size-11 flex-col items-center justify-center gap-0.5 rounded-xl border bg-ink-900/95 leading-none shadow-lg focus-visible:outline-2 focus-visible:outline-violet-400 disabled:opacity-40',
          tempo !== 'ifs' || aberta ? 'border-violet-300 text-white' : 'border-ink-600 text-ink-100',
        )}
        style={{ top: TOPO_DO_BOTAO }}
      >
        <span className="text-[11px] font-semibold">{atual.curto}</span>
        <ChevronDown className={clsx('size-3 text-violet-300 transition-transform', aberta && 'rotate-180')} aria-hidden />
        {novidade && <span className="absolute top-1 left-1 size-1.5 rounded-full bg-violet-300 ring-2 ring-ink-900" aria-hidden />}
      </button>
      {aberta && (
        <div
          ref={caixa}
          id={id}
          role="dialog"
          aria-label="Modelo do tempo"
          data-caixa-modelo=""
          onKeyDown={teclas}
          // À esquerda do botão. Deitado (pouca altura), a partir do topo e com rolagem, para caber.
          className="absolute right-[calc(60px+env(safe-area-inset-right))] z-30 w-[min(17rem,calc(100vw-76px))] overflow-y-auto overscroll-contain rounded-xl border border-violet-500/50 bg-ink-900 py-1 shadow-2xl"
          style={
            deitado
              ? { top: 'calc(env(safe-area-inset-top) + 8px)', maxHeight: 'calc(100dvh - env(safe-area-inset-top) - 16px)' }
              : { top: TOPO_DO_BOTAO, maxHeight: `calc(100dvh - ${TOPO_DO_BOTAO} - 8px)` }
          }
        >
          <p className="px-3 pt-1.5 pb-1 text-[11px] font-medium text-ink-300">Modelo do tempo</p>
          <div role="radiogroup" aria-label="Modelo do tempo">
            {modelos.map((m) => {
              const marcado = tempo === m;
              const s = SOBRE_MODELO_TEMPO[m];
              return (
                <button
                  key={m}
                  ref={(el) => {
                    opcoes.current[m] = el;
                  }}
                  type="button"
                  role="radio"
                  aria-checked={marcado}
                  aria-label={`${s.nome}${s.ia ? ' (IA)' : ''}: ${s.detalhe}`}
                  data-opcao-modelo={m}
                  onClick={() => {
                    fechar();
                    trocar(m);
                  }}
                  className={clsx(
                    'flex min-h-11 w-full items-center gap-2.5 px-3 text-left focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-violet-400',
                    marcado ? 'bg-violet-600/25' : 'hover:bg-ink-800 active:bg-ink-800',
                  )}
                >
                  <span
                    className={clsx(
                      'inline-flex size-4 shrink-0 items-center justify-center rounded-full border',
                      marcado ? 'border-violet-300 bg-violet-500 text-white' : 'border-ink-500',
                    )}
                    aria-hidden
                  >
                    {marcado && <Check className="size-3" />}
                  </span>
                  <span className="w-11 shrink-0 text-sm font-semibold text-white">
                    {s.curto}
                    {s.ia && <span className="ml-0.5 text-[10px] font-normal text-violet-200">IA</span>}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-xs text-ink-300">{s.linha}</span>
                  {m !== 'ifs' && novidade && !marcado && <span className="size-1.5 shrink-0 rounded-full bg-violet-300" aria-hidden />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
